import Button from 'atoms/Button/button';
import { getEventEntryAccessOptions } from 'components/community/editPublicAirmeet/constants';
import WithTooltip from 'components/general/WithTooltip';
import { MODAL_TYPE_EVENT_ENTRY } from 'components/modals';
import { Box, FlexDiv } from 'components/molecules/utils/basicComponents';
import Tooltip from 'components/tooltip/Tooltip';
import Icon from 'foundations/icon';
import { Typography } from 'foundations/typography';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showAppModal } from 'store/actions/layout';
import styled from 'styled-components';
import { AIRMEET_STATUS } from 'utils/constants/airmeet';

const EVENT_ENTRY_ACCESS_OPTIONS = getEventEntryAccessOptions();

export default function EventEntryField({
    eventEntryValue,
    setEventEntryValue,
    domainList,
    setDomainList,
    editMode,
    airmeetId,
}) {
    const dispatch = useDispatch();
    const hide_auth_providers = useSelector(
        (state) => state?.lounge?.airmeet?.hide_auth_providers
    );
    const hideAuthProviders = hide_auth_providers && editMode;
    const ticketsConfigured = useSelector(
        (state) => state?.lounge?.airmeet?.ticketsInfo?.ticketsConfigured
    );
    const airmeetStatus = useSelector(
        (state) => state?.lounge?.airmeet?.status
    );
    const hasTicketing = ticketsConfigured && editMode;

    const eventEntryOptionLabel = useMemo(() => {
        if (hasTicketing) return 'Ticket holders and invited participants';

        let eventEntryOption = null;
        Object.keys(EVENT_ENTRY_ACCESS_OPTIONS || []).forEach((key) => {
            if (EVENT_ENTRY_ACCESS_OPTIONS?.[key]?.value === eventEntryValue) {
                eventEntryOption = EVENT_ENTRY_ACCESS_OPTIONS?.[key];
            }
        });
        return eventEntryOption?.label;
    }, [eventEntryValue, hasTicketing]);

    const radioChangeHandler = (value, domainList) => {
        setEventEntryValue(value);
        setDomainList(domainList);
    };

    const showEventEntryModal = () => {
        dispatch(
            showAppModal(MODAL_TYPE_EVENT_ENTRY, {
                radioChangeHandler: radioChangeHandler,
                eventEntryValue,
                domains: domainList || [],
                hideAuthProviders,
                airmeetId,
            })
        );
    };

    return (
        <Box py={3}>
            <FlexDiv alignItems={'center'} mb={'24px'}>
                <Box>
                    <Typography variant={'subtext0'} color={'ambience.1'}>
                        Who can attend this event? (You can edit this later) *
                    </Typography>
                    <Box mt={'4px'} display={'flex'} flexWrap={'wrap'}>
                        <Typography
                            variant={'subtext2-bold'}
                            color={'ambience.0'}
                        >
                            {eventEntryOptionLabel}
                        </Typography>
                        {airmeetStatus !== AIRMEET_STATUS.FINISHED && (
                            <WithTooltip
                                tooltip={
                                    hasTicketing &&
                                    'Event entry cannot be changed as it is a ticketed event'
                                }
                                place='bottom'
                            >
                                <StyledButton
                                    variant={'link'}
                                    color={'accentPrimary.0'}
                                    ml={'4px'}
                                    onClick={showEventEntryModal}
                                    disabled={hasTicketing}
                                    isDisabled={hasTicketing}
                                >
                                    Change
                                </StyledButton>
                            </WithTooltip>
                        )}
                    </Box>
                </Box>
                <Box ml='6px'>
                    <Tooltip
                        place='right'
                        toolClass={'attendeeTheme custom-arrow-curve'}
                        renderElement={
                            <TooltipContent>
                                <Typography
                                    variant='subtext0'
                                    color='ambience.19'
                                >
                                    Configure how participants can discover and
                                    enter the event
                                </Typography>
                            </TooltipContent>
                        }
                        renderTrigger={
                            <Icon
                                icon='ic-info'
                                fill='ambience.6'
                                minWidth={16}
                                width={16}
                                height={16}
                            />
                        }
                    />
                </Box>
            </FlexDiv>
        </Box>
    );
}

export const TooltipContent = styled(Box)`
    padding: 16px 24px;
    background: ${({ theme }) => theme.colors.ambience[0]};
    border-radius: 8px;
    max-width: 262px;
`;

export const StyledButton = styled(Button)`
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    p {
        color: ${({ theme, isDisabled }) =>
            isDisabled && theme.colors.ambience[14]};
    }
`;
